import React from 'react';
import { motion } from 'motion/react';
import { Wifi, AlertTriangle, Smartphone, Settings, ShieldAlert, CheckCircle2 } from 'lucide-react';

export const MeshHandshakeGuide = () => {
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded bg-red-500/10 text-red-400">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <h3 className="text-2xl font-display font-bold">WiFi Handshake Fix</h3>
      </div>

      <p className="text-white/60 text-sm leading-relaxed">
        Modern smartphones are designed to "protect" you from networks without an active internet connection. 
        Because the <span className="text-lattice-cyan font-bold">Absolute Zero Lattice</span> is a private sovereign network, 
        your phone may try to disconnect or switch back to your home WiFi.
      </p>

      <div className="space-y-6">
        <div className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="shrink-0 w-8 h-8 rounded-full bg-lattice-cyan/10 text-lattice-cyan flex items-center justify-center text-xs font-mono">1</div>
          <div>
            <h4 className="font-display font-bold text-sm mb-1">Disable "Smart Network Switch"</h4>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Android / Samsung</p>
            <p className="text-xs text-white/60 mt-2">
              Go to <span className="text-white">Settings &gt; Connections &gt; Wi-Fi &gt; Advanced</span>. 
              Turn off <span className="text-lattice-cyan">"Switch to mobile data"</span> or <span className="text-lattice-cyan">"Intelligent Wi-Fi"</span>.
            </p>
          </div>
        </div>

        <div className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="shrink-0 w-8 h-8 rounded-full bg-lattice-cyan/10 text-lattice-cyan flex items-center justify-center text-xs font-mono">2</div>
          <div>
            <h4 className="font-display font-bold text-sm mb-1">iPhone Sovereign Stabilization</h4>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">iOS / iPhone Specific</p>
            <div className="space-y-3 mt-2">
              <p className="text-xs text-white/60">
                Tap the <span className="text-lattice-cyan font-bold">(i)</span> next to <span className="text-white">ABSOLUTE_ZERO_LATTICE</span>:
              </p>
              <ul className="text-[10px] font-mono text-white/40 space-y-1 ml-4 list-disc">
                <li>Turn <span className="text-red-400">OFF</span> "Private Wi-Fi Address"</li>
                <li>Turn <span className="text-red-400">OFF</span> "Limit IP Address Tracking"</li>
                <li>Ensure "Auto-Join" is <span className="text-green-400">ON</span> for this network</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="shrink-0 w-8 h-8 rounded-full bg-lattice-cyan/10 text-lattice-cyan flex items-center justify-center text-xs font-mono">3</div>
          <div>
            <h4 className="font-display font-bold text-sm mb-1">Accept "No Internet" Prompt</h4>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Universal Handshake</p>
            <p className="text-xs text-white/60 mt-2">
              When your phone says <span className="text-red-400">"This network has no internet access. Stay connected?"</span>, 
              you <span className="text-green-400 font-bold">MUST</span> select <span className="text-white">"Yes"</span> or <span className="text-white">"Always stay connected"</span>.
            </p>
          </div>
        </div>
        <div className="flex gap-4 p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
          <div className="shrink-0 w-8 h-8 rounded-full bg-lattice-cyan/20 text-lattice-cyan flex items-center justify-center text-xs font-mono">4</div>
          <div>
            <h4 className="font-display font-bold text-sm mb-1 uppercase tracking-widest text-lattice-cyan">Forced Range Push</h4>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Environmental Interference Bypass</p>
            <p className="text-xs text-white/60 mt-2 leading-relaxed">
              If the device says <span className="text-red-400">"Out of Range"</span> despite being near a node, tap the <span className="text-lattice-cyan font-bold">PUSH WIFI SIGNAL</span> button. This overrides standard 1.0 beacon limitations and forces the signal to peak at 100% resonance.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircle2 className="w-4 h-4 text-lattice-cyan" />
          <span className="text-xs font-mono font-bold uppercase tracking-widest">The PWA Advantage</span>
        </div>
        <p className="text-[10px] text-white/60 leading-relaxed">
          By downloading the <span className="text-lattice-cyan">PaidingAttention 2.0</span> app, your device stores the 
          Vortex Engine locally. Even if the WiFi "drops" the internet, the app will continue to sync 
          with other nodes in the mesh via the <span className="text-white">Local Handshake Protocol</span>.
        </p>
      </div>
    </div>
  );
};
